import { useMutation, useQueryClient } from "@tanstack/react-query";
import fetchToBackend, { type BackendError } from "@/utils/fetchToBackend";
import {
  contractingKeys,
  dashboardKeys,
  profileItemKeys,
  searchProfileKeys,
} from "@/lib/query-keys";
import { toastUtil } from "@/utils/toastUtil";
import { capture } from "@/lib/posthog";
import { useUpsell } from "@/providers/UpsellProvider";
import type { SearchProfile } from "@/types/domain";

export function useCreateSearchProfile() {
  const qc = useQueryClient();
  const openUpsell = useUpsell();
  return useMutation<
    SearchProfile,
    BackendError,
    // El wizard guarda con la pantalla de fondo a la vista: refrescarla a mitad
    // se ve como si la app se moviera sola. Refresca él al terminar.
    string | { name: string; deferRefresh?: boolean }
  >({
    mutationFn: (vars) =>
      fetchToBackend<SearchProfile>("/searchprofiles", {
        method: "POST",
        body: JSON.stringify({
          name: typeof vars === "string" ? vars : vars.name,
        }),
      }),
    onSuccess: (_data, vars) => {
      capture("search_profile_created");
      if (typeof vars !== "string" && vars.deferRefresh) return;
      qc.invalidateQueries({ queryKey: searchProfileKeys.all });
    },
    onError: (err) => {
      // El tope de líneas de negocio es del plan: en vez de un toast, el modal
      // dice cuántas permite el suyo y cuántas daría el siguiente.
      if (err?.errorCode === "SEARCH_PROFILE_LIMIT") {
        openUpsell("profile-limit");
        return;
      }
      toastUtil("No se pudo crear la línea de negocio.", "error");
    },
  });
}

export function useUpdateSearchProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      id: number;
      data: Partial<SearchProfile>;
      deferRefresh?: boolean;
    }) =>
      fetchToBackend<SearchProfile>(`/searchprofiles/${vars.id}`, {
        method: "PUT",
        body: JSON.stringify(vars.data),
      }),
    onSuccess: (_data, vars) => {
      if (vars.deferRefresh) return;
      // Mandar `searchprofile_keywords` reescribe `searchprofileitem` en el
      // backend: sin esto el panel sigue mostrando la lista de items anterior.
      if ("searchprofile_keywords" in vars.data) {
        qc.invalidateQueries({ queryKey: profileItemKeys.all });
      }
      // La lista y los gráficos no se recargan por los filtros: viajan en su URL y
      // la línea nueva ya los pide. Recargarlos aquí los pedía con los viejos.
      // El cruce automático sí cambia la lista sin cambiar sus filtros.
      if ("searchprofile_checkqualification" in vars.data) {
        qc.invalidateQueries({ queryKey: contractingKeys.lists() });
        qc.invalidateQueries({ queryKey: dashboardKeys.all });
      }
      // Se espera: quien guarda sabe que, al terminar, la línea ya es la nueva.
      return qc.invalidateQueries({ queryKey: searchProfileKeys.all });
    },
  });
}

export function useDeleteSearchProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      fetchToBackend(`/searchprofiles/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: searchProfileKeys.all }),
  });
}
